// Dependencies
import styled, { css, keyframes } from "styled-components";

// Types
import { VirtualAssistantChatMessageProps } from "./VirtualAssistantChat.types";

// Utils
import { theme } from "config/theme";

const getBackgroundStyles = (isLoading: boolean) => {
    if (!!isLoading) {
        return css`
            background: linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #f0f1f7 0%, #ccc 100%) border-box;
            box-shadow: 0px 6px 12px 0px #cccccc80;
        `;
    }

    return css`
        background: linear-gradient(#fff, #fff) padding-box,
            linear-gradient(135deg, ${theme.colors.brand[100]} 0%, ${theme.colors.secondary[100]} 100%) border-box;
        box-shadow: 0px 6px 12px 0px ${theme.colors.brand[100]}80;
    `;
};

type VirtualAssistantChatClosedChatIconWrapperStyleProps = {
    isLoading: boolean;
};

export const VirtualAssistantChatClosedChatIconWrapper = styled.div<VirtualAssistantChatClosedChatIconWrapperStyleProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fdf4f5;
    width: 56px;
    height: 56px;
    border-radius: 100px;
    border: 1px solid transparent;

    position: fixed;
    bottom: 16px;
    right: 16px;

    cursor: pointer;

    transition: transform 0.2s;

    &:hover {
        transform: scale(1.05);
    }

    ${({ isLoading }) => getBackgroundStyles(isLoading)}
`;

const transitionAnimation = keyframes`
  from {
    transform: translateY(50%);
  }

  to {
    transform: translateY(0);
  }
`;

export const VirtualAssistantChatMobileWrapper = styled.div`
    display: flex;
    width: 100%;

    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;

    z-index: 9999;

    animation-name: ${transitionAnimation};
    animation-duration: 0.5s;
`;

export const VirtualAssistantChatContainer = styled.div`
    width: 100%;
    max-height: 622px;
    padding: 24px;
    background-color: #fff;
    border-radius: 16px 16px 0 0;

    display: flex;
    flex-direction: column;
    gap: 24px;

    position: relative;

    @media (min-width: 768px) {
        width: 360px;
        border-radius: 16px;
        border: 1px solid ${theme.colors.brand[100]};
        box-shadow: 0px 6px 12px 0px #ced0dd;

        position: fixed;
        right: 16px;
        bottom: 16px;

        z-index: 2;
    }
`;

export const VirtualAssistantChatHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

export const VirtualAssistantChatMessageSpinnerWrapper = styled.div`
    position: relative;
    padding: 63px 0;
`;

export const VirtualAssistantChatLabelsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

export const VirtualAssistantChatAssistantName = styled.p`
    font-family: Nunito;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    text-align: left;
`;

export const VirtualAssistantChatVersionLabelWrapper = styled.div`
    background-color: #feefd2;
    border-radius: 100px;
    padding: 2px 8px;
`;

export const VirtualAssistantChatVersionLabel = styled.p`
    font-family: Nunito;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.02em;
    color: #965307;
    text-align: left;
    text-transform: uppercase;
`;

export const VirtualAssistantChatCloseIcon = styled.img`
    width: 16px;
    cursor: pointer;
`;

type VirtualAssistantChatMessageCompositionWrapperStyleProps = {
    hasMessage: boolean;
};

export const VirtualAssistantChatMessageCompositionWrapper = styled.div<VirtualAssistantChatMessageCompositionWrapperStyleProps>`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden auto;
    gap: 24px;
    padding-right: 16px;
`;

export const VirtualAssistantChatTextAreaWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px 16px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #ced0dd;
    background-color: #fafafc;
    gap: 8px;
    position: relative;
`;

export const VirtualAssistantChatTextAreaOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #fafafc;
    border-radius: 8px;
    border: 2px solid #fafafc;
    opacity: 0.8;
`;

export const VirtualAssistantChatTextArea = styled.textarea`
    font-family: Nunito Sans;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    color: #25272d;
    background-color: transparent;
    width: 100%;
    padding-right: 4px;
    max-height: 110px;
    resize: none;
`;

export const VirtualAssistantChatSendMessageButton = styled.button`
    width: 24px;
    cursor: pointer;
`;

type VirtualAssistantChatMessageContainerStyleProps = {
    variant: VirtualAssistantChatMessageProps["variant"];
};

export const VirtualAssistantChatMessageContainer = styled.div<VirtualAssistantChatMessageContainerStyleProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    ${({ variant }) =>
        variant === "loading" &&
        css`
            align-items: center;
        `}
        
    ${({ variant }) =>
        variant === "default" &&
        css`
            align-items: flex-start;
        `}

    ${({ variant }) =>
        variant === "highlight" &&
        css`
            align-items: flex-start;
            padding: 8px 12px;
            width: 240px;
            align-self: flex-end;
            border-radius: 8px;
            background-color: #f0f1f7;
        `}
`;

type VirtualAssistantChatMessageContentWrapperStyleProps = {
    hasSpacing: boolean;
};

export const VirtualAssistantChatMessageContentWrapper = styled.div<VirtualAssistantChatMessageContentWrapperStyleProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    ${({ hasSpacing }) =>
        !!hasSpacing &&
        css`
            gap: 8px;
        `}
`;

type VirtualAssistantChatMessageContentStyleProps = {
    variant: VirtualAssistantChatMessageProps["variant"];
};

export const VirtualAssistantChatMessageContent = styled.p<VirtualAssistantChatMessageContentStyleProps>`
    font-family: Nunito Sans;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;

    ${({ variant }) =>
        variant === "loading" &&
        css`
            color: #9c9fb0;
        `}

    ${({ variant }) =>
        variant !== "loading" &&
        css`
            color: #25272d;
        `}
`;

export const VirtualAssistantChatMessageAvatarWrapper = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 100%;
    border: 1px solid ${theme.colors.brand[100]};
`;

export const VirtualAssistantChatActionCompositionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2px;
`;

type VirtualAssistantChatMessageIconContainerStyleProps = {
    isActive: boolean;
};

export const VirtualAssistantChatMessageIconContainer = styled.div<VirtualAssistantChatMessageIconContainerStyleProps>`
    display: flex;
    align-items: center;
    justify-content: center;

    width: 32px;
    height: 32px;
    border-radius: 8px;

    transition: background-color 0.2s ease-in-out;
    cursor: pointer;

    ${({ isActive }) =>
        !!isActive &&
        css`
            background-color: #f0f1f7;

            &:hover {
                background-color: transparent;
            }
        `}

    ${({ isActive }) =>
        !isActive &&
        css`
            background-color: transparent;

            &:hover {
                background-color: #f0f1f7;
            }
        `}
`;

export const VirtualAssistantChatSuggestionMessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    border-radius: 8px;
    padding: 12px;
    gap: 8px;
    background-color: #fafafc;
`;

export const VirtualAssistantChatSuggestionMessageLabel = styled.p`
    font-family: Nunito;
    font-weight: 600;
    font-size: 14px;
    line-height: 22px;
`;

export const VirtualAssistantChatSuggestionMessageWrapper = styled.div`
    width: 100%;
    border-radius: 8px;
    padding: 12px;
    gap: 8px;
    background-color: #ffffff;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.2s ease-in-out;

    &:hover {
        border: 1px solid #ced0dd;
    }
`;

export const VirtualAssistantChatSuggestionMessageTitle = styled.p`
    font-family: Nunito Sans;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
`;

const ellipsis1 = keyframes`
   0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
`;

const ellipsis2 = keyframes`
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(12px, 0);
  }
`;

const ellipsis3 = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
`;

export const VirtualAssistantChatLoader = styled.div`
    display: flex;
    position: absolute;

    > div {
        display: inline-block;
        position: relative;
        width: 40px;
        height: 13px;

        > div {
            position: absolute;
            top: 3.25px;
            width: 6.5px;
            height: 6.5px;
            border-radius: 50%;
            background: #ced0dd;
            animation-timing-function: cubic-bezier(0, 1, 1, 0);
        }

        > div:nth-child(1) {
            left: 4px;
            animation: ${ellipsis1} 0.6s infinite;
        }

        > div:nth-child(2) {
            left: 4px;
            animation: ${ellipsis2} 0.6s infinite;
        }

        > div:nth-child(3) {
            left: 16px;
            animation: ${ellipsis2} 0.6s infinite;
        }

        > div:nth-child(4) {
            left: 28px;
            animation: ${ellipsis3} 0.6s infinite;
        }
    }
`;
